// @ts-nocheck
import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState
} from 'react'
import cx from 'classnames'

import ClientOnlyPortal from './ClientOnlyPortal'
import { CloseIcon } from 'components/common'
import s from './Modal.module.css'

interface Props {
  children: React.ReactNode
  contentLabel?: string
  shouldCloseOnOverlayClick?: boolean
  isOpen?: boolean
  role?: string
  onAfterClose?: () => void
  onAfterOpen?: () => void
  onRequestClose?: () => void
  shouldCloseOnEsc?: boolean
  shouldFocusAfterRender?: boolean
  showCloseIcon?: boolean
  headerActive?: boolean
  className?: string
  overflowContent?: boolean
}

export type ModalRef = {
  openModal: () => void
  closeModal: () => void
}

const Modal = forwardRef<ModalRef, Props>(
  (
    {
      children,
      contentLabel,
      isOpen,
      role,
      onAfterClose,
      onAfterOpen,
      onRequestClose,
      shouldCloseOnEsc,
      shouldCloseOnOverlayClick,
      shouldFocusAfterRender,
      showCloseIcon,
      headerActive,
      className,
      overflowContent
    },
    ref
  ) => {
    const [display, setDisplay] = useState(isOpen)
    const overlayRef = useRef(null)
    const contentRef = useRef(null)

    const open = () => {
      setDisplay(true)
    }

    const close = () => {
      setDisplay(false)
    }

    const ownerHandlesClose = () => onRequestClose

    const requestClose = (event) => ownerHandlesClose() && onRequestClose(event)

    const handleKeyDown = (event) => {
      if (shouldCloseOnEsc && event.keyCode === 27) {
        event.stopPropagation()
        if (ownerHandlesClose()) {
          requestClose(event)
        } else {
          close()
        }
      }
    }

    const contentHasFocus = () =>
      document?.activeElement === contentRef.current ||
      contentRef.current.contains(document?.activeElement)

    const focusContent = () =>
      contentRef?.current && !contentHasFocus() && contentRef.current.focus()

    const handleOverlayOnClick = (event) => {
      if (shouldCloseOnOverlayClick) {
        if (ownerHandlesClose()) {
          requestClose(event)
        } else {
          close()
        }
      }
    }

    const handleOverlayOnMouseDown = (event) => {
      if (!shouldCloseOnOverlayClick && event.target === overlayRef.current) {
        event.preventDefault()
      }
    }

    const closeIconOnClick = (event) => {
      if (ownerHandlesClose()) {
        requestClose(event)
      } else {
        close()
      }
    }

    // Focus only needs to be set once when the modal is being opened
    useEffect(() => {
      if (shouldFocusAfterRender && display) {
        focusContent()
      }

      if (onAfterOpen && display) {
        onAfterOpen()
      }

      if (onAfterClose && !display) {
        onAfterClose()
      }
    }, [display])

    useEffect(() => {
      setDisplay(isOpen)
    }, [isOpen])

    useImperativeHandle(ref, () => {
      return {
        openModal: () => open(),
        closeModal: () => close()
      }
    })

    const contentClass = cx(s.content, {
      [className]: className,
      ['overflow']: overflowContent
    })

    return (
      <>
        {display && (
          <ClientOnlyPortal selector="#modal">
            <div className={s.wrapper}>
              <div
                className={s.backdrop}
                onClick={handleOverlayOnClick}
                onMouseDown={handleOverlayOnMouseDown}
                ref={overlayRef}
              />
              <div
                className={contentClass}
                ref={contentRef}
                role={role}
                aria-label={contentLabel}
                onKeyDown={handleKeyDown}
                tabIndex="-1"
              >
                {headerActive ? (
                  <div className={s.header}>
                    {contentLabel}
                    {showCloseIcon && (
                      <CloseIcon
                        className={`icon-inline ${s.closeIcon}`}
                        onClick={closeIconOnClick}
                      />
                    )}
                  </div>
                ) : null}
                {children}
              </div>
            </div>
          </ClientOnlyPortal>
        )}
      </>
    )
  }
)

Modal.defaultProps = {
  contentLabel: '',
  isOpen: false,
  onAfterOpen: null,
  onAfterClose: null,
  onRequestClose: null,
  role: 'dialog',
  shouldCloseOnEsc: true,
  shouldCloseOnOverlayClick: true,
  shouldFocusAfterRender: true,
  showCloseIcon: true,
  headerActive: true,
  className: null,
  overflowContent: false
}

export default Modal
